import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useAutocomplete, { AutocompleteGetTagProps } from '@mui/material/useAutocomplete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { autocompleteClasses } from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { indexFundService, productTypeService } from '../../api';
import { ProductTypeSelectedIndexFund } from '../../types/productType';
import { IndexFund } from '../../types/indexFund';

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_MS = 800;
const MIN_SEARCH_LENGTH = 2;

const Root = styled('div')(({ theme }) => ({
  width: '100%',
  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.85)',
  fontSize: '14px',
}));

const Label = styled('label')`
  padding: 0 0 4px;
  line-height: 1.5;
  display: block;
`;

const InputWrapper = styled('div')(({ theme }) => ({
  width: '100%',
  minHeight: 38,
  border: '1px solid',
  borderColor: theme.palette.mode === 'dark' ? '#434343' : '#d9d9d9',
  backgroundColor: theme.palette.mode === 'dark' ? '#141414' : '#fff',
  borderRadius: '4px',
  padding: '4px 6px',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '4px',
  boxSizing: 'border-box',
  '&:hover': {
    borderColor: theme.palette.mode === 'dark' ? '#177ddc' : '#40a9ff',
  },
  '&.focused': {
    borderColor: theme.palette.mode === 'dark' ? '#177ddc' : '#40a9ff',
    boxShadow: '0 0 0 2px rgb(24 144 255 / 0.2)',
  },
  '& input': {
    backgroundColor: theme.palette.mode === 'dark' ? '#141414' : '#fff',
    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,.85)',
    height: '28px',
    boxSizing: 'border-box',
    padding: '2px 4px',
    width: '0',
    minWidth: '80px',
    flexGrow: 1,
    border: 0,
    margin: 0,
    outline: 0,
    fontSize: '14px',
  },
}));

interface TagItemProps extends ReturnType<AutocompleteGetTagProps> {
  label: string;
}

function TagItem(props: TagItemProps) {
  const { label, onDelete, ...other } = props;
  return (
    <div {...other}>
      <span className="tag-label">{label}</span>
      <CloseIcon
        className="tag-delete"
        fontSize="small"
        onClick={onDelete}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onDelete(e);
        }}
        tabIndex={-1}
        aria-label={`Remove ${label}`}
      />
    </div>
  );
}

const StyledTag = styled(TagItem)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  maxWidth: '100%',
  minHeight: 26,
  lineHeight: '22px',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#fafafa',
  border: `1px solid ${theme.palette.mode === 'dark' ? '#303030' : '#e8e8e8'}`,
  borderRadius: '2px',
  boxSizing: 'border-box',
  padding: '0 2px 0 10px',
  outline: 0,
  overflow: 'hidden',
  flexShrink: 0,
  '&:focus-visible': {
    borderColor: theme.palette.mode === 'dark' ? '#177ddc' : '#40a9ff',
    backgroundColor: theme.palette.mode === 'dark' ? '#003b57' : '#e6f7ff',
  },
  '& .tag-label': {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    fontSize: '14px',
    fontWeight: 500,
    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.88)' : 'rgba(0,0,0,0.85)',
    paddingRight: 4,
  },
  '& .tag-delete': {
    fontSize: '16px',
    cursor: 'pointer',
    padding: '2px',
    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)',
    flexShrink: 0,
    '&:hover': {
      color: theme.palette.mode === 'dark' ? '#fff' : 'rgba(0,0,0,0.75)',
    },
  },
}));

const Listbox = styled('ul')(({ theme }) => ({
  width: '100%',
  margin: '2px 0 0',
  padding: 0,
  position: 'absolute',
  listStyle: 'none',
  backgroundColor: theme.palette.mode === 'dark' ? '#141414' : '#fff',
  overflow: 'auto',
  maxHeight: '250px',
  borderRadius: '4px',
  boxShadow: '0 2px 8px rgb(0 0 0 / 0.15)',
  zIndex: 1,
  '& li': {
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    '& .option-label': {
      flexGrow: 1,
      fontSize: '14px',
    },
    '& .option-check': {
      flexShrink: 0,
      width: 24,
      height: 24,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  '& li[data-in-value="true"]': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(24, 144, 255, 0.12)' : 'rgba(24, 144, 255, 0.06)',
    '& .option-check .MuiSvgIcon-root': {
      color: theme.palette.primary.main,
      opacity: 1,
    },
  },
  "& li[aria-selected='true']": {
    fontWeight: 600,
  },
  [`& li.${autocompleteClasses.focused}`]: {
    backgroundColor: theme.palette.mode === 'dark' ? '#003b57' : '#e6f7ff',
    cursor: 'pointer',
  },
}));

function mapIndexFund(f: IndexFund): ProductTypeSelectedIndexFund {
  return { id: f.id, nameEn: f.nameEn, nameAr: f.nameAr, code: f.code };
}

function buildFilterJson(search: string) {
  if (!search.trim()) return '{"items":[]}';
  return JSON.stringify({
    items: [{ value: search.trim(), columnField: 'text', operatorValue: 'contains' }],
  });
}

function normalizeSelectedFundsResponse(
  data: ProductTypeSelectedIndexFund[] | { items: ProductTypeSelectedIndexFund[]; totalItems?: number } | undefined,
): ProductTypeSelectedIndexFund[] {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.items)) return data.items;
  return [];
}

function orderSelectedValueIds(selectedIds: number[], preferredOrder: number[]): number[] {
  const preferred = new Set(preferredOrder);
  const first: number[] = [];
  const rest: number[] = [];
  for (const id of preferredOrder) {
    if (selectedIds.includes(id)) first.push(id);
  }
  for (const id of selectedIds) {
    if (!preferred.has(id)) rest.push(id);
  }
  return [...first, ...rest];
}

export interface ProductTypeIndexFundsAutocompleteProps {
  id: string;
  label: string;
  /** Selected index fund ids (Formik field) */
  value: number[];
  onChange: (ids: number[]) => void;
  mode: 'create' | 'edit';
  productTypeId?: number;
  disabled?: boolean;
  /** When false, skip loading (e.g. Custom Product off) */
  enabled: boolean;
}

export function ProductTypeIndexFundsAutocomplete({
  id,
  label,
  value: selectedIds,
  onChange,
  mode,
  productTypeId,
  disabled,
  enabled,
}: ProductTypeIndexFundsAutocompleteProps) {
  const [entityMap, setEntityMap] = useState(() => new Map<number, ProductTypeSelectedIndexFund>());
  const [selectedOrder, setSelectedOrder] = useState<number[]>([]);
  const [browseOrder, setBrowseOrder] = useState<number[]>([]);
  const [browseOffset, setBrowseOffset] = useState(0);
  const [totalBrowse, setTotalBrowse] = useState(0);
  const [loadingSelected, setLoadingSelected] = useState(false);
  const [loadingBrowse, setLoadingBrowse] = useState(false);
  const [search, setSearch] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const hasHydratedInitialSelectionRef = useRef(false);

  const upsertEntities = useCallback((items: ProductTypeSelectedIndexFund[]) => {
    setEntityMap((prev) => {
      const next = new Map(prev);
      for (const it of items) next.set(it.id, it);
      return next;
    });
  }, []);

  const resetLocalState = useCallback(() => {
    setEntityMap(new Map());
    setSelectedOrder([]);
    setBrowseOrder([]);
    setBrowseOffset(0);
    setTotalBrowse(0);
  }, []);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search]);

  const fetchBrowsePage = useCallback(
    async (offset: number, append: boolean) => {
      if (disabled || !enabled) return;
      setLoadingBrowse(true);
      try {
        if (debouncedSearch.trim().length > 0 && debouncedSearch.trim().length < MIN_SEARCH_LENGTH) {
          setBrowseOffset(0);
          setTotalBrowse(0);
          setBrowseOrder([]);
          return;
        }

        const filter = buildFilterJson(debouncedSearch);
        const res = await indexFundService.fetchIndexFunds({ limit: PAGE_SIZE, offset, filter });
        const items = (res.data?.items || []).map(mapIndexFund);
        const total = res.data?.totalItems ?? 0;
        upsertEntities(items);
        setTotalBrowse(total);
        setBrowseOffset(offset + items.length);
        setBrowseOrder((prev) => {
          const ids = items.map((i) => i.id);
          if (!append) return ids;
          const seen = new Set(prev);
          const merged = [...prev];
          for (const i of ids) {
            if (!seen.has(i)) {
              seen.add(i);
              merged.push(i);
            }
          }
          return merged;
        });
      } finally {
        setLoadingBrowse(false);
      }
    },
    [debouncedSearch, disabled, enabled, upsertEntities],
  );

  useEffect(() => {
    if (!enabled || disabled) {
      resetLocalState();
      return;
    }
    hasHydratedInitialSelectionRef.current = false;
    resetLocalState();
    if (mode === 'edit' && productTypeId) {
      setLoadingSelected(true);
      (async () => {
        try {
          const res = await productTypeService.fetchProductTypeSelectedIndexFunds(productTypeId);
          const items = normalizeSelectedFundsResponse(res.data);
          upsertEntities(items);
          setSelectedOrder(items.map((i) => i.id));
        } finally {
          setLoadingSelected(false);
        }
      })();
    }
  }, [enabled, disabled, mode, productTypeId, resetLocalState, upsertEntities]);

  useEffect(() => {
    if (mode !== 'edit') return;
    if (hasHydratedInitialSelectionRef.current) return;
    if (selectedIds.length > 0) return;
    if (!selectedOrder.length) return;
    onChange(selectedOrder);
    hasHydratedInitialSelectionRef.current = true;
  }, [mode, selectedIds.length, selectedOrder, onChange]);

  useEffect(() => {
    if (!enabled || disabled) return;
    setBrowseOrder([]);
    setBrowseOffset(0);
    setTotalBrowse(0);
    fetchBrowsePage(0, false).catch(() => undefined);
  }, [enabled, disabled, debouncedSearch, fetchBrowsePage]);

  const mergedOptions = useMemo(() => {
    const out: ProductTypeSelectedIndexFund[] = [];
    for (const fundId of browseOrder) {
      const o = entityMap.get(fundId);
      if (o) out.push(o);
    }
    return out;
  }, [browseOrder, entityMap]);

  const orderedValueIds = useMemo(
    () => orderSelectedValueIds(selectedIds, selectedOrder),
    [selectedIds, selectedOrder],
  );

  const valueObjects = useMemo(
    () =>
      orderedValueIds.map((fundId) => {
        const found = entityMap.get(fundId);
        if (found) return found;
        return { id: fundId, nameEn: 'Loading…', nameAr: '', code: '' };
      }),
    [orderedValueIds, entityMap],
  );

  const getOptionLabel = useCallback((option: ProductTypeSelectedIndexFund) => {
    const code = option.code ? ` (${option.code})` : '';
    return `${option.nameEn}${code}`;
  }, []);

  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getTagProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    focused,
    setAnchorEl,
    popupOpen,
  } = useAutocomplete<ProductTypeSelectedIndexFund, true, false, false>({
    id,
    multiple: true,
    options: mergedOptions,
    value: valueObjects,
    disabled: disabled || !enabled,
    getOptionLabel,
    isOptionEqualToValue: (a, b) => a.id === b.id,
    filterOptions: (opts) => opts,
    onChange: (_e, newValue, reason) => {
      onChange(newValue.map((o) => o.id));
      if (reason === 'selectOption') {
        setSearch('');
      }
    },
    onInputChange: (_e, newInput, reason) => {
      // Ignore "reset" so selecting/options updates don't wipe user search text
      // and don't trigger a second empty-query request.
      if (reason === 'input') setSearch(newInput);
      if (reason === 'clear') setSearch('');
    },
    inputValue: search,
  });

  const listboxProps = getListboxProps();
  const flatGrouped = useMemo(() => {
    if (!groupedOptions.length) return [];
    if (typeof (groupedOptions[0] as any)?.group === 'string') return [];
    return groupedOptions as ProductTypeSelectedIndexFund[];
  }, [groupedOptions]);

  const handleListScroll = useCallback(
    (event: React.UIEvent<HTMLUListElement>) => {
      const el = event.currentTarget;
      if (el.scrollTop + el.clientHeight < el.scrollHeight - 24) return;

      const browseHasMore = browseOffset < totalBrowse;

      if (browseHasMore && !loadingBrowse) {
        fetchBrowsePage(browseOffset, true).catch(() => undefined);
      }
    },
    [browseOffset, totalBrowse, loadingBrowse, fetchBrowsePage],
  );

  const showListLoader = loadingSelected || loadingBrowse;

  return (
    <Root style={{ position: 'relative', marginBottom: 16, marginTop: 8 }}>
      <div {...getRootProps()}>
        <Label {...getInputLabelProps()}>{label}</Label>
        <InputWrapper ref={setAnchorEl} className={focused ? 'focused' : ''}>
          {valueObjects.map((option, index) => {
            const { key, ...itemProps } = getTagProps({ index });
            return <StyledTag key={key} {...itemProps} label={getOptionLabel(option)} />;
          })}
          <input {...getInputProps()} placeholder={valueObjects.length ? '' : 'Search and select index funds'} />
        </InputWrapper>
      </div>
      {popupOpen && flatGrouped.length > 0 ? (
        <Listbox
          {...listboxProps}
          onScroll={(e) => {
            listboxProps.onScroll?.(e as any);
            handleListScroll(e);
          }}
        >
          {flatGrouped.map((option, index) => {
            const optionProps = getOptionProps({ option, index });
            const inSelection = selectedIds.includes(option.id);
            return (
              <li key={option.id} {...optionProps} data-in-value={inSelection ? 'true' : 'false'}>
                <span className="option-label">{getOptionLabel(option)}</span>
                <span className="option-check" aria-hidden>
                  {inSelection ? (
                    <CheckIcon className="MuiSvgIcon-root" fontSize="small" color="primary" />
                  ) : (
                    <Box sx={{ width: 20, height: 20 }} />
                  )}
                </span>
              </li>
            );
          })}
          {showListLoader ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={22} />
            </Box>
          ) : null}
        </Listbox>
      ) : null}
      {popupOpen && !flatGrouped.length && !showListLoader ? (
        <Listbox {...listboxProps}>
          <Box sx={{ px: 1.5, py: 1 }}>
            <Typography variant="body2" color="text.secondary">
              No index funds found
            </Typography>
          </Box>
        </Listbox>
      ) : null}
    </Root>
  );
}
